/* eslint-disable no-loop-func */
// Package Imports
import React, { forwardRef } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import MaterialTable from "@material-table/core";
import { PropagateLoader, BeatLoader } from "react-spinners";
import { MdError } from "react-icons/md";
import { ExportCsv, ExportPdf } from "@material-table/exporters";
import {
  AddBox,
  ArrowDownward,
  Check,
  ChevronLeft,
  ChevronRight,
  Clear,
  DeleteOutline,
  Edit,
  FilterList,
  FirstPage,
  LastPage,
  Remove,
  SaveAlt,
  Search,
  ViewColumn,
} from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import OfflineBoltIcon from "@mui/icons-material/OfflineBolt";
import CancelIcon from "@mui/icons-material/Cancel";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import BoltIcon from "@mui/icons-material/Bolt";

// Local Imports
import "./../style/Station.css";
import { StationNumbers } from "./../lib/Calculations";
import { FilterStations } from "./../lib/Filtering";
import { Box, Button, ButtonGroup, Chip, Avatar } from "@mui/material";

// Constants
const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => (
    <KeyboardArrowDownIcon {...props} ref={ref} />
  )),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};
const TableTheme = createTheme({
  palette: {
    primary: {
      main: "#009fda",
    },
    secondary: {
      main: "#009fda",
    },
  },
  overrides: {
    MuiAutocomplete: {
      option: {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        width: "auto",
        display: "block",
        fontSize: "1rem",
        boxSizing: "border-box",
        minHeight: "48px",
        fontFamily: "Arial, sans-serif",
        fontWeight: "400",
        lineHeight: "1.5",
        paddingTop: "6px",
        letterSpacing: "0.00938em",
        paddingBottom: "6px",
      },
    },
    MuiCheckbox: {
      root: {
        color: "#3E3E3E",
        "&$checked": {
          color: "#3E3E3E",
        },
      },
    },
    MuiChip: {
      root: {
        backgroundColor: "#32908F !important",
        color: "#fff !important",
        height: "40px",
      },
      avatar: {
        color: "#fff !important",
        height: "20px",
      },
      sizeSmall: {
        backgroundColor: "#de746e !important",
        color: "#fff !important",
        height: "40px",
        marginTop: "10px",
      },
    },
    MuiIconButton: {
      root: {
        backgroundColor: "transparent !important",
        color: "#3E3E3E !important",
      },
    },
    MuiInput: {
      underline: {
        "&:before": {
          borderBottom: "1px solid #3E3E3E",
        },
        "&:hover": {
          borderBottom: "2px solid #3E3E3E",
        },
      },
    },
    MuiInputAdornment: {
      positionStart: {
        color: "#3E3E3E !important",
      },
    },
    MuiPopover: {
      paper: {
        backgroundColor: "#424242 !important",
      },
    },
    MuiTableRow: {
      hover: {
        "&$hover:hover": {
          backgroundColor: "#32908F",
        },
      },
    },
    MuiTableCell: {
      body: {
        color: "#3E3E3E !important",
        backgroundColor: "#F7F8F9 !important",
      },
      head: {
        color: "#3E3E3E !important",
        backgroundColor: "#F7F8F9 !important",
        fontFamily: "Arial !important",
      },
      stickyHeader: {
        color: "#3E3E3E !important",
        background: "#F7F8F9 !important",
        fontFamily: "Arial !important",
      },
    },
    MuiToolbar: {
      root: {
        color: "#3E3E3E !important",
        backgroundColor: "transparent !important",
      },
    },
    MuiTableSortLabel: {
      active: {
        color: "#32908F !important",
      },
    },
    MuiInputBase: {
      input: {
        paddingLeft: "5px !important",
        paddingRight: "5px !important",
        color: "#3E3E3E !important",
      },
    },
    MuiTypography: {
      alignCenter: {
        color: "#3E3E3E !important",
      },
      body2: {
        color: "#3E3E3E !important",
      },
      caption: {
        color: "#3E3E3E !important",
      },
    },
    MuiSelect: {
      select: {
        minWidth: "150px",
      },
    },
    MuiMenu: {
      list: {
        minWidth: "150px",
      },
    },
    MuiSwitch: {
      switchBase: {
        color: "#44D74B !important",
        "&$checked": {
          color: "#44D74B !important",
        },
        "&$checked + $track": {
          backgroundColor: "#44D74B !important",
        },
      },
    },
  },
});

const columns = [
  {
    field: "station_id",
    title: "Station ID",
    //width: "auto",
    whiteSpace: "nowrap",
  },
  {
    field: "station_name",
    title: "Station Name",
    width: 200,
    style: {
      minWidth: 200,
    },
  },
  {
    field: "status",
    title: "Status",
    filtering: false,
    align: "left",
    width: 100,
    style: {
      paddingRight: 0,
    },
    headerStyle: {
      textAlign: "center",
    },
    cellStyle: {
      // textAlign: "-webkit-center",
    },
    render: (rowData) => {
      return StationNumbers([rowData]).unreachablePorts > 0 ? (
        rowData.vendor_error_code ? (
          <Chip
            size="small"
            avatar={<MdError />}
            label={`Error Code: ${rowData.vendor_error_code}`}
          />
        ) : (
          <MdError style={{ color: "#DA5952", fontSize: "20px" }} />
        )
      ) : StationNumbers([rowData]).inUsePorts > 0 ? (
        rowData.energy && rowData.current_kw && rowData.vehicle_soc ? (
          <Chip
            className="activeChip"
            size="small"
            avatar={<BoltIcon style={{ color: "#fff", fontSize: "20px" }} />}
            label={`${rowData.vehicle_soc} % / ${rowData.current_kw.toFixed(
              1
            )} kW / ${rowData.energy.toFixed(2)} kWh`}
          />
        ) : rowData.current_kw && rowData.energy ? (
          <Chip
            className="activeChip"
            size="small"
            avatar={<BoltIcon style={{ color: "#fff", fontSize: "20px" }} />}
            label={`${rowData.current_kw.toFixed(
              1
            )} kW / ${rowData.energy.toFixed(2)} kWh`}
          />
        ) : rowData.energy ? (
          <Chip
            className="activeChip"
            size="small"
            avatar={<BoltIcon style={{ color: "#fff", fontSize: "20px" }} />}
            label={`${rowData.energy.toFixed(2)} kWh`}
          />
        ) : (
          <OfflineBoltIcon style={{ color: "#C78500", fontSize: "20px" }} />
        )
      ) : (
        <CheckCircleIcon style={{ color: "#68BB45", fontSize: "20px" }} />
      );
    },
  },
  {
    field: "address",
    title: "Address",
    width: 250,
  },
  {
    field: "city",
    title: "City",
    width: 250,
  },
  {
    field: "energy_cost",
    title: "Energy Cost",
    style: {
      minWidth: 50,
    },
  },
  {
    field: "idle_cost",
    title: "Idle Cost",
    style: {
      minWidth: 50,
    },
  },
];

class StationContainer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      statuses: [],

      // Station List
      stationList: [],
      totalStations: [],
      totalPorts: <BeatLoader color="#9e9e9e" size={5} speedMultiplier={0.8} />,
      availableStations: [],
      availablePorts: (
        <BeatLoader color="#9e9e9e" size={5} speedMultiplier={0.8} />
      ),
      inUseStations: [],
      inUsePorts: <BeatLoader color="#9e9e9e" size={5} speedMultiplier={0.8} />,
      unreachableStations: [],
      unreachablePorts: (
        <BeatLoader color="#9e9e9e" size={5} speedMultiplier={0.8} />
      ),
      stationSelection: "total",
    };
  }

  async componentDidMount() {
    if (Object.keys(this.props.stationStatus).length !== 0) this._getData();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.stationStatus !== this.props.stationStatus &&
      Object.keys(this.props.stationStatus).length !== 0
    )
      this._getData();
  }

  _getData = () => {
    // Filter stations via header filter
    var filtered = FilterStations(
      this.props.stationStatus,
      [],
      [],
      [],
      [],
      "All Groups"
    ); // Returns sessions, statuses, pctStatus, totalSessions, active
    this.setState(
      {
        ...StationNumbers(filtered.statuses, this.props.active), // Sets totalStations, availableStations, inUseStations, unreachableStations, totalPorts, availablePorts, inUsePorts, unreachablePorts
        loading: false,
      },
      () => {
        // Set stationList
        this.changeStationFilter(this.state.stationSelection);
      }
    );
  };

  changeStationFilter = (status) => {
    // Define which stations are listed
    switch (status) {
      case "available":
        this.setState({ stationList: this.state.availableStations });
        break;
      case "inUse":
        this.setState({ stationList: this.state.inUseStations });
        break;
      case "faulted":
        this.setState({ stationList: this.state.unreachableStations });
        break;
      default:
        this.setState({ stationList: this.state.totalStations });
        break;
    }
    this.setState({ stationSelection: status });
  };

  typeSelect = () => {
    return (
      <div>
        <ButtonGroup>
          <Button
            onClick={() => this.changeStationFilter("total")}
            className={
              this.state.stationSelection === "total"
                ? "activeButtonGroup"
                : "buttonGroup"
            }
          >
            Total Ports
          </Button>
          <Button
            onClick={() => this.changeStationFilter("available")}
            className={
              this.state.stationSelection === "available"
                ? "activeButtonGroup"
                : "buttonGroup"
            }
          >
            Available
          </Button>
          <Button
            onClick={() => this.changeStationFilter("inUse")}
            className={
              this.state.stationSelection === "inUse"
                ? "activeButtonGroup"
                : "buttonGroup"
            }
          >
            In Use
          </Button>
          <Button
            onClick={() => this.changeStationFilter("faulted")}
            className={
              this.state.stationSelection === "faulted"
                ? "activeButtonGroup"
                : "buttonGroup"
            }
          >
            Faults
          </Button>
        </ButtonGroup>
      </div>
    );
  };

  render() {
    let pageSize = 10;
    let pageSizeArray = [10, 25, 50];

    if (this.state.stationList > 0) {
      if (this.state.stationList.length < 25) {
        pageSize = this.state.stationList.length;
        pageSizeArray = [25];
      }
    }

    return (
      <div
        style={{
          backgroundColor: "#f7f8f9",
          height: "100%",
          width: "100%",
          paddingTop: "40px",
          paddingLeft: "60px",
          paddingRight: "60px",
          paddingBottom: "40px",
        }}
      >
        <span
          style={{ color: "#404040", fontWeight: "bold", fontSize: "36px" }}
        >
          Station List
        </span>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          //justifyContent="space-around"
          style={{
            backgroundColor: "#fff",
            boxShadow: "0px 3px 3px #dedede",
            borderRadius: 4,
            padding: "35px",
            marginTop: "48px",
          }}
        >
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            style={{ width: "80%" }}
          >
            <Box
              display="flex"
              flexDirection="column"
              style={{ marginRight: "50px", minWidth: "180px" }}
            >
              <span className="smallTitle">Total Ports</span>
              <Box display="flex" flexDirection="row" alignItems="center">
                <span className="bigDetail">{this.state.totalPorts || 0}</span>
              </Box>
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              style={{ marginRight: "50px", minWidth: "160px" }}
            >
              <span className="smallTitle">Available Ports</span>
              <Box display="flex" flexDirection="row" alignItems="center">
                <CheckCircleIcon
                  style={{
                    color: "#68BB45",
                    fontSize: "20px",
                    marginRight: "10px",
                  }}
                />
                <span className="bigDetail">
                  {this.state.availablePorts || 0}
                </span>
              </Box>
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              style={{ marginRight: "50px", minWidth: "170px" }}
            >
              <span className="smallTitle">In Use Ports</span>
              <Box display="flex" flexDirection="row" alignItems="center">
                <OfflineBoltIcon
                  style={{
                    color: "#C78500",
                    fontSize: "20px",
                    marginRight: "10px",
                  }}
                />
                <span className="bigDetail">{this.state.inUsePorts || 0}</span>
              </Box>
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              style={{ marginRight: "0px" }}
            >
              <span className="smallTitle">Faulted Ports</span>
              <Box display="flex" flexDirection="row" alignItems="center">
                <CancelIcon
                  style={{
                    color: "#DA5952",
                    fontSize: "20px",
                    marginRight: "10px",
                  }}
                />
                <span className="bigDetail">
                  {this.state.unreachablePorts || 0}
                </span>
              </Box>
            </Box>
          </Box>
        </Box>

        <div className="row mr-1 ml-1 mt-3" style={{ display: "block" }}>
          <div>
            {this.state.loading ? (
              <div
                className="col-3"
                style={{ marginLeft: "45vw", marginTop: "25vh" }}
              >
                <PropagateLoader color="#9e9e9e" speedMultiplier={0.8} />
              </div>
            ) : (
              <ThemeProvider theme={TableTheme}>
                <MaterialTable
                  columns={columns}
                  data={this.state.stationList}
                  title={this.typeSelect()}
                  icons={tableIcons}
                  options={{
                    maxBodyHeight: "calc(45vh)",

                    rowStyle: {
                      backgroundColor: "#FFF",
                      color: "#3E3E3E",
                      fontSize: "13px",
                    },
                    headerStyle: {
                      backgroundColor: "#0073a8",
                      color: "#ffffff",
                      fontWeight: "bold",
                      position: "sticky",
                      height: 52,
                    },
                    searchFieldStyle: {
                      border: "#C4C8C7 solid 1px",
                      borderRadius: 4,
                      padding: "3px",
                      marginRight: 20,
                    },
                    exportAllData: true,
                    exportButton: true,
                    exportMenu: [
                      {
                        label: "Export PDF",
                        //// You can do whatever you wish in this function. We provide the
                        //// raw table columns and table data for you to modify, if needed.
                        // exportFunc: (cols, datas) => console.log({ cols, datas })
                        exportFunc: (cols, datas) =>
                          ExportPdf(cols, datas, "myPdfFileName"),
                      },
                      {
                        label: "Export CSV",
                        exportFunc: (cols, datas) =>
                          ExportCsv(cols, datas, "myCsvFileName"),
                      },
                    ],
                    pageSize: pageSize,
                    pageSizeOptions: pageSizeArray,
                    filtering: true,
                    grouping: true,
                    filterCellStyle: {
                      backgroundColor: "#FFF",
                      color: "#3E3E3E",
                      fontSize: "11px",
                    },
                  }}
                />
              </ThemeProvider>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default StationContainer;
